import { RouterProvider } from 'react-router-dom'
import RootRouter from './routes/root.route'
import {
  persist,
  RootState,
  store,
  useAppDispatch,
  useAppSelector,
} from './utils/agent.store'
import { USER_PROFILES } from './utils/constants'
import {
  LoginTypeEnum,
  setAuthProfile,
  clearForceMsalPromptNextLogin,
  fetchAndResolveAuthFeature,
} from '@app-store'
import { useEffect, useState } from 'react'
import { environment } from './utils/environment'
import { performAgentDeskLogout } from './session/agentLogout'
import SessionIdleCountdownProvider from './session/SessionInactivityManager'
import MandatoryReloginManager from './session/MandatoryReloginManager'
import { invalidateToken } from './utils/helperFunctions'
import {
  subscribeCrossTabLogout,
  subscribeCrossTabLoginSuccess,
} from '@ecrm-session'
import { allowMsalShellForCrossTabLoginFollowAfterLogout } from '@ecrm-msal'
import {
  clearSessionPostLogoutReturnPath,
  getSessionPostLogoutReturnPath,
  isSafeReturnPath,
} from './utils/postLogoutReturnPath'

const RouterWrapper = () => {
  const dispatch = useAppDispatch()
  const [authResolved, setAuthResolved] = useState(false)

  const userSuperAdmin = useAppSelector(
    (state: RootState) => state.authUser.user?.superAdmin
  )
  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )

  // The feature endpoint is the source of truth for the login provider. Do not
  // mount an auth guard until this request settles; a fallback guard could
  // redirect the document and abort the feature request. MSAL initialization
  // and redirect handling still run before React renders in main.tsx, preserving
  // the existing tab-scoped new-tab behavior once MSAL_SPA is selected.
  useEffect(() => {
    let active = true
    const fallback = environment.authProvider ?? ''
    void fetchAndResolveAuthFeature(fallback).finally(() => {
      if (active) setAuthResolved(true)
    })

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    return subscribeCrossTabLogout((loginType) => {
      const t = store.getState().session.token
      void performAgentDeskLogout({
        dispatch: store.dispatch,
        persist,
        token: t,
        loginType: loginType ?? LoginTypeEnum.ADMIN_FORCED_LOGOUT,
        broadcastCrossTab: false,
        invalidateToken: invalidateToken,
      })
    })
  }, [])

  useEffect(() => {
    return subscribeCrossTabLoginSuccess(() => {
      if (store.getState().authUser.isAuthenticated !== false) return
      if (window.location.pathname !== '/logout') return

      allowMsalShellForCrossTabLoginFollowAfterLogout()
      const path = getSessionPostLogoutReturnPath()
      clearSessionPostLogoutReturnPath()
      store.dispatch(clearForceMsalPromptNextLogin())
      const origin = window.location.origin
      const target =
        path && isSafeReturnPath(path) ? `${origin}${path}` : origin
      void persist.flush().then(() => {
        window.location.href = target
      })
    })
  }, [])

  useEffect(() => {
    if (window.location.href.includes('/error')) return
    const authState = store.getState().authUser
    const userData = authState.user
    if (!userData) return
    if (userData?.clients?.length === 0)
      window.location.href = window.location.origin + `/error/user-not-found`
    if (userData?.profile === USER_PROFILES.ACCESS_MANAGER) {
      window.location.href = window.location.origin + `/error/user-not-found`
      return
    }
    if (userData?.profile === USER_PROFILES.SUPPORT) {
      const filteredClients =
        userData?.clients?.filter(
          (client) => client?.agent_visibility === true
        ) || []
      dispatch(
        setAuthProfile({
          ...authState,
          user: {
            ...userData,
            superAdmin: false,
            clients: filteredClients,
          },
        })
      )
      if (filteredClients.length === 0) {
        window.location.href = window.location.origin + `/error/user-not-found`
      }
    }
  }, [userSuperAdmin, dispatch])

  useEffect(() => {
    if (!selectedTenantId) return
    if (!('serviceWorker' in navigator)) return

    const sendMessage = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_ACTIVE_CLIENT',
          clientId: String(selectedTenantId),
        })
      }
    }

    if (navigator.serviceWorker.controller) {
      sendMessage()
    } else {
      navigator.serviceWorker.ready.then(sendMessage)
    }
  }, [selectedTenantId])

  if (!authResolved) return null

  return (
    <SessionIdleCountdownProvider>
      <RouterProvider router={RootRouter} />
      <MandatoryReloginManager />
    </SessionIdleCountdownProvider>
  )
}

export default RouterWrapper
